/**
 * AM Dashboard — "My Clients" page
 * 
 * Shows assigned clients for the logged-in Account Manager.
 * Admins see an admin-view banner and unassigned client count.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AMLayout } from "@/components/am/AMLayout";
import { useAccountManager } from "@/hooks/useAccountManager";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Users, FileText, AlertCircle, Search, ChevronRight, CheckSquare, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import ClientHealthBadge from "@/components/am/ClientHealthBadge";
import { useSLAMonitor } from "@/hooks/useSLAMonitor";
import { useAMTasks } from "@/hooks/useAMTasks";
import { useTaskAutomation } from "@/hooks/useTaskAutomation";

interface AssignedClient {
  agent_id: string;
  assigned_at: string;
  agent_name: string;
  agent_company: string | null;
  agent_vertical: string;
  agent_is_active: boolean;
  agent_tier: string;
  agent_licensed_states_count: number;
  health_score?: number;
  health_status?: 'healthy' | 'neutral' | 'at_risk' | 'critical';
  health_delta_7d?: number | null;
}

export default function AMDashboard() {
  const navigate = useNavigate();
  const { accountManager, loading: amLoading } = useAccountManager();
  const { role } = useAdmin();
  const isAdminViewing = role === "admin";

  const [clients, setClients] = useState<AssignedClient[]>([]);
  const [activeContracts, setActiveContracts] = useState(0);
  const [newClientIds, setNewClientIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expiringContracts, setExpiringContracts] = useState<{ agentId: string; agentName: string; daysRemaining: number }[]>([]);

  // SLA + Tasks + Automation
  const clientAgentIds = clients.map((c) => c.agent_id);
  const { slaStatuses, breachCount, warningCount } = useSLAMonitor(accountManager?.id || null, clientAgentIds);
  const { tasks } = useAMTasks(accountManager?.id || null);
  const openTaskCount = tasks.filter((t) => t.status !== "done").length;

  // Build clientHealth map for task automation (includes grace period flag)
  const clientHealth = new Map(
    clients
      .filter((c) => c.health_status)
      .map((c) => [c.agent_id, { status: c.health_status!, name: c.agent_name, isNew: newClientIds.has(c.agent_id) }])
  );

  // Fetch expiring contracts via RPC
  useEffect(() => {
    if (!accountManager?.id) return;
    supabase
      .rpc("get_expiring_contracts", { p_am_id: accountManager.id })
      .then(({ data, error }) => {
        if (error) {
          console.error("[AMDashboard] get_expiring_contracts error:", error);
          return;
        }
        setExpiringContracts(
          (data || []).map((c: { agent_id: string; agent_name: string; days_remaining: number }) => ({
            agentId: c.agent_id,
            agentName: c.agent_name,
            daysRemaining: c.days_remaining,
          }))
        );
      });
  }, [accountManager?.id]);

  // Auto-create tasks based on health drops, SLA breaches, and expiring contracts
  useTaskAutomation({
    amId: accountManager?.id || null,
    tasks,
    clientHealth,
    slaStatuses,
    expiringContracts,
    enabled: !loading && !amLoading && !!accountManager,
  });

  useEffect(() => {
    if (amLoading) return;
    fetchData();

    // Refresh dashboard data every 5 minutes to pick up health score updates
    const interval = setInterval(() => {
      fetchData(true);
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [accountManager, amLoading]);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (accountManager) {
        const { data: assignments } = await supabase
          .from("client_am_assignments")
          .select("agent_id, assigned_at")
          .eq("am_id", accountManager.id)
          .eq("status", "active");

        if (assignments && assignments.length > 0) {
          const agentIds = assignments.map((a) => a.agent_id);

          const { data: agents } = await supabase
            .from("agents")
            .select("id, name, company, vertical, is_active, tier, licensed_states")
            .in("id", agentIds);

          const merged: AssignedClient[] = assignments.map((a) => {
            const agent = (agents || []).find((ag) => ag.id === a.agent_id);
            return {
              agent_id: a.agent_id,
              assigned_at: a.assigned_at,
              agent_name: agent?.name || "Unknown",
              agent_company: agent?.company || null,
              agent_vertical: agent?.vertical || "unknown",
              agent_is_active: agent?.is_active ?? false,
              agent_tier: agent?.tier || "Bronze",
              agent_licensed_states_count: agent?.licensed_states?.length || 0,
            };
          });

          // Fetch latest health scores for all clients
          const { data: healthScores } = await supabase
            .from("client_health_scores")
            .select("agent_id, overall_score, health_status, score_delta_7d, computed_at")
            .in("agent_id", agentIds)
            .order("computed_at", { ascending: false });

          const latestScores = new Map<string, { score: number; status: string; delta: number | null }>();
          (healthScores || []).forEach((hs) => {
            if (!latestScores.has(hs.agent_id)) {
              latestScores.set(hs.agent_id, {
                score: hs.overall_score,
                status: hs.health_status,
                delta: hs.score_delta_7d,
              });
            }
          });

          const mergedWithHealth = merged.map((c) => {
            const hs = latestScores.get(c.agent_id);
            return {
              ...c,
              health_score: hs?.score,
              health_status: hs?.status as AssignedClient['health_status'],
              health_delta_7d: hs?.delta,
            };
          });
          setClients(mergedWithHealth);

          const { data: activeContractData } = await supabase
            .from("contracts")
            .select("id, agent_id, cycle_start")
            .in("agent_id", agentIds)
            .eq("status", "active");
          setActiveContracts(activeContractData?.length || 0);

          // Determine new clients (earliest active contract < 7 days old)
          const GRACE_DAYS = 7;
          const now = Date.now();
          const earliestByAgent = new Map<string, number>();
          (activeContractData || []).forEach((c) => {
            const start = new Date(c.cycle_start).getTime();
            const prev = earliestByAgent.get(c.agent_id);
            if (!prev || start < prev) earliestByAgent.set(c.agent_id, start);
          });
          const newIds = new Set<string>();
          earliestByAgent.forEach((start, agentId) => {
            if ((now - start) / (1000 * 60 * 60 * 24) < GRACE_DAYS) newIds.add(agentId);
          });
          setNewClientIds(newIds);
        } else {
          setClients([]);
          setActiveContracts(0);
        }
      }

    } catch (err) {
      console.error("[AMDashboard] Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatVertical = (v: string) => {
    const map: Record<string, string> = {
      life_insurance: "Life Insurance",
      mortgage: "Mortgage",
    };
    return map[v] || v;
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case "Elite": return "bg-purple-100 text-purple-700 border-purple-200";
      case "Gold": return "bg-amber-100 text-amber-700 border-amber-200";
      case "Silver": return "bg-slate-100 text-slate-700 border-slate-200";
      default: return "bg-orange-100 text-orange-700 border-orange-200";
    }
  };

  if (amLoading || loading) {
    return (
      <AMLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </AMLayout>
    );
  }

  return (
    <AMLayout>
      {isAdminViewing && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-[#D4A853]/30 bg-[#D4A853]/5 px-4 py-3 text-sm text-[#D4A853]">
          <AlertCircle className="h-4 w-4" />
          Admin View — You are viewing the AM portal as an administrator.
        </div>
      )}

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">My Clients</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your assigned client portfolio
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid gap-4 mb-8 grid-cols-2 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Clients</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{clients.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Contracts</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{activeContracts}</div>
          </CardContent>
        </Card>

        <Link to="/am/tasks">
          <Card className="hover:bg-muted/30 transition-colors cursor-pointer h-full">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Open Tasks</CardTitle>
              <CheckSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{openTaskCount}</div>
            </CardContent>
          </Card>
        </Link>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">SLA Alerts</CardTitle>
            <Clock className={`h-4 w-4 ${breachCount > 0 ? "text-red-500" : "text-muted-foreground"}`} />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <span className={`text-3xl font-bold ${breachCount > 0 ? "text-red-600" : ""}`}>
                {breachCount + warningCount}
              </span>
              {breachCount > 0 && (
                <span className="text-xs text-red-600">{breachCount} breach</span>
              )}
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Client Table */}
      {clients.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">
              No clients assigned yet. Contact your administrator to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Client Portfolio</CardTitle>
              <div className="relative w-56">
                <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-9 text-sm"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-3 font-medium">Client Name</th>
                    <th className="pb-3 font-medium">Company</th>
                    <th className="pb-3 font-medium">Vertical</th>
                    <th className="pb-3 font-medium">Tier</th>
                    <th className="pb-3 font-medium">Licensed States</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Health</th>
                    <th className="pb-3 font-medium">Assigned Date</th>
                    <th className="pb-3 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {clients
                    .filter((c) => {
                      if (!searchQuery) return true;
                      const q = searchQuery.toLowerCase();
                      return (
                        c.agent_name.toLowerCase().includes(q) ||
                        (c.agent_company || "").toLowerCase().includes(q) ||
                        c.agent_vertical.toLowerCase().includes(q) ||
                        c.agent_tier.toLowerCase().includes(q)
                      );
                    })
                    .map((client) => (
                    <tr
                      key={client.agent_id}
                      className="border-b last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/am/clients/${client.agent_id}`)}
                    >
                      <td className="py-3 font-medium text-foreground">{client.agent_name}</td>
                      <td className="py-3 text-muted-foreground">{client.agent_company || "—"}</td>
                      <td className="py-3">{formatVertical(client.agent_vertical)}</td>
                      <td className="py-3">
                        <Badge className={getTierColor(client.agent_tier)} variant="outline">
                          {client.agent_tier}
                        </Badge>
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {client.agent_licensed_states_count} state{client.agent_licensed_states_count !== 1 ? "s" : ""}
                      </td>
                      <td className="py-3">
                        <Badge variant={client.agent_is_active ? "default" : "secondary"}>
                          {client.agent_is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="py-3">
                        {client.health_score !== undefined ? (
                          <ClientHealthBadge
                            score={client.health_score}
                            status={client.health_status!}
                            delta7d={client.health_delta_7d}
                            size="sm"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {client.assigned_at ? format(new Date(client.assigned_at), "MMM d, yyyy") : "—"}
                      </td>
                      <td className="py-3">
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </AMLayout>
  );
}
